import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Callout, EmptyState, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { PageHeader } from 'components/header/PageHeader'
import { DEFAULT_NUMBER_OF_ROWS } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import type { Limit } from 'types/paymentLimits'
import mockPaymentLimits from '../../../__mocks__/PaymentLimits/paymentLimits.json'
import { EditLimitSheet } from './components/EditLimitSheet'
import { PaymentLimitCard } from './components/PaymentLimitCard'

export const PaymentLimits = () => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()

  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const {
    data: limitsData,
    isFetching,
    isError,
  } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    page: 0,
    size: Number(DEFAULT_NUMBER_OF_ROWS),
  })

  const limits = limitsData?.content ?? (mockPaymentLimits?.content as Limit[])

  const hasLimits = limits?.length > 0
  const showEmptyState = !isFetching && !isError && limits?.length === 0

  const handleEditLimit = useCallback((limit: Limit) => {
    setSelectedLimit(limit)
    setIsSheetOpen(true)
  }, [])

  const handleCloseSheet = useCallback(() => {
    setIsSheetOpen(false)
    setSelectedLimit(null)
  }, [])

  return (
    <>
      <PageHeader description={t('paymentLimits.pageDescription')} />

      <Box paddingB={24}>
        <Callout>{t('paymentLimits.customerLimitsCallout')}</Callout>
      </Box>

      {isFetching && <RowsSkeletonLoader rows={3} />}

      {hasLimits && (
        <Box flex isFlexWrap gap={16}>
          {limits?.map(limit => (
            <Box key={limit.ruleId} className="w-full md:w-[calc(50%-8px)]">
              <PaymentLimitCard limit={limit} onEditLimit={handleEditLimit} />
            </Box>
          ))}
        </Box>
      )}

      {showEmptyState && (
        <EmptyState
          testId="payment-limits-empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('paymentLimits.emptyStateTitle')}
          description={t('paymentLimits.emptyStateDescription')}
        />
      )}

      <EditLimitSheet isOpen={isSheetOpen} limit={selectedLimit} onClose={handleCloseSheet} />
    </>
  )
}
